/**
 * 前端共享工具函数
 *
 * 主要导出：
 * - `isNetworkError` — 判断错误消息是否为网络相关错误
 * - `formatFileSize` — 格式化文件大小（自动选择 B/KB/MB/GB 单位）
 * - `sanitizeEmailHtml` — 使用 DOMPurify 净化不可信邮件 HTML
 * - `cleanQuoteHtml` — 清理并净化引用邮件 HTML
 */

import DOMPurify from "dompurify";

/**
 * 判断错误消息是否为网络错误（包含 fetch 或 network 关键词）。
 * 用于在组件中区分网络错误（静默处理）和业务错误（显示 toast）。
 * @param msg - 错误消息字符串
 * @returns 是否为网络错误
 */
export const isNetworkError = (msg: string) =>
  msg.includes("fetch") || msg.includes("network");

/**
 * 格式化文件大小 — 自动选择合适单位（B/KB/MB/GB）。
 * @param bytes - 文件字节数
 * @returns 格式化后的字符串，如 "1.5 MB"
 */
export function formatFileSize(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let size = bytes;
  // 逐级除以 1024 直到找到合适的单位
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  // B 不保留小数，其他单位保留一位小数
  return i === 0 ? `${size} ${units[i]}` : `${size.toFixed(1)} ${units[i]}`;
}

/**
 * 使用 DOMPurify 净化不可信邮件 HTML。
 * 保留邮件展示需要的常规标签、内联样式和链接 target，移除脚本、事件属性和危险 URL。
 * @param html - 原始邮件 HTML
 * @returns 可安全插入 DOM 的 HTML 字符串
 */
export function sanitizeEmailHtml(html: string): string {
  return DOMPurify.sanitize(html, { ADD_ATTR: ["target"], FORCE_BODY: true });
}

/**
 * 清理并净化引用邮件 HTML。
 * 先移除邮件客户端生成的冗余 style/colgroup/col 标签，再统一执行安全消毒。
 * @param html - 原始邮件 HTML
 * @returns 清理并消毒后的 HTML 字符串
 */
export function cleanQuoteHtml(html: string): string {
  const cleaned = html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<colgroup[^>]*>[\s\S]*?<\/colgroup>/gi, "")
    .replace(/<col[^>]*>/gi, "");
  return sanitizeEmailHtml(cleaned);
}
